import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  message,
  Typography,
  Row,
  Col,
  Statistic,
  Input,
  DatePicker,
  Modal,
  Select,
  Badge,
  Tooltip
} from 'antd';
import {
  SearchOutlined,
  CheckOutlined,
  EyeOutlined,
  CalendarOutlined,
  UserOutlined,
  BankOutlined,
  ReloadOutlined,
  FileExcelOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
// import * as XLSX from 'xlsx'; // Removed due to security vulnerability
import { withdrawalService } from '../../services';

const { Text, Title } = Typography;
const { RangePicker } = DatePicker;
const { TextArea } = Input;

const WithdrawalList = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState({
    total_records: 0,
    pending_count: 0,
    approved_count: 0,
    pending_amount: 0,
    approved_amount: 0
  });
  
  // Filters
  const [dateRange, setDateRange] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  

  
  // Modal states
  const [approvalModalVisible, setApprovalModalVisible] = useState(false);
  const [approvalNote, setApprovalNote] = useState('');
  const [approvalLoading, setApprovalLoading] = useState(false);



  // Load data on component mount
  useEffect(() => {
    fetchWithdrawals();
  }, []);

  const fetchWithdrawals = async () => {
    setLoading(true);
    
    try {
      const token = localStorage.getItem('accessToken');
      
      const params = {
        accessToken: token,
        ...(dateRange.length === 2 && {
          dateFrom: dateRange[0].format('YYYY-MM-DD'),
          dateTo: dateRange[1].format('YYYY-MM-DD')
        }),
        ...(statusFilter && {
          trangThai: statusFilter
        })
      };
      
      const response = await withdrawalService.getWithdrawalHistory(params);
      
      if (response.success) {
        let filteredData = response.data.withdrawal_history || [];
        
        // Lọc theo trạng thái phía client nếu server chưa hỗ trợ
        if (statusFilter && params.trangThai) {
          filteredData = filteredData.filter(item => item.withdrawal?.status === statusFilter);
        }
        
        setData(filteredData);
        setSummary(response.data.summary || {});
        message.success(`${response.message} (${filteredData.length} bản ghi)`);
      } else {
        message.error(response.message || 'Lỗi không xác định');
      }
    } catch (error) {
      console.error('Error fetching withdrawals:', error);
      message.error('Lỗi kết nối API');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveWithdrawalsSimulate = () => {
    // Chức năng duyệt tạm thời khi API chưa sẵn sàng
    const pendingItems = data.filter(item => item.withdrawal?.status === 'Đợi Duyệt');
    
    Modal.confirm({
      title: 'Mô phỏng duyệt rút tiền',
      content: (
        <div>
          <p>Chức năng này chỉ mô phỏng vì API chưa được implement.</p>
          <p>Sẽ "duyệt" <strong>{pendingItems.length}</strong> yêu cầu rút tiền.</p>
          {approvalNote && (
            <p>Ghi chú: <em>{approvalNote}</em></p>
          )}
        </div>
      ),
      okText: 'Mô phỏng duyệt',
      cancelText: 'Hủy',
      onOk: () => {
        // Simulate approval
        const updatedData = data.map(item => {
          if (item.withdrawal?.status === 'Đợi Duyệt') {
            return {
              ...item,
              withdrawal: {
                ...item.withdrawal,
                status: 'Đã Duyệt',
                note: item.withdrawal.note + (approvalNote ? ` | ${approvalNote}` : ' | Đã duyệt (mô phỏng)')
              },
              approved_info: {
                approved_at: new Date().toISOString(),
                approved_by: 'Admin (Mô phỏng)'
              }
            };
          }
          return item;
        });
        
        setData(updatedData);
        
        // Update summary
        const newSummary = {
          ...summary,
          pending_count: 0,
          approved_count: summary.total_records,
          pending_amount: 0,
          approved_amount: summary.pending_amount + summary.approved_amount
        };
        setSummary(newSummary);
        
        message.success(`Đã mô phỏng duyệt ${pendingItems.length} yêu cầu rút tiền`);
        
        setApprovalModalVisible(false);
        setApprovalNote('');
      }
    });
  };

  const handleApproveWithdrawals = async () => {
    setApprovalLoading(true);
    
    try {
      const token = localStorage.getItem('accessToken');
      const params = {
        accessToken: token,
        ...(dateRange.length === 2 && {
          dateFrom: dateRange[0].format('YYYY-MM-DD'),
          dateTo: dateRange[1].format('YYYY-MM-DD')
        }),
        ...(approvalNote && {
          ghiChu: approvalNote
        })
      };
      
      const response = await withdrawalService.approveWithdrawals(params);
      
      if (response.success) {
        message.success(response.message);
        setApprovalModalVisible(false);
        setApprovalNote('');
        fetchWithdrawals(); // Reload data
      } else {
        message.error(response.message || 'Lỗi không xác định');
      }
    } catch (error) {
      console.error('Error approving withdrawals:', error);
      
      // Kiểm tra nếu là lỗi 404 hoặc 500 thì có thể API chưa implement
      if (error.response?.status === 404) {
        message.warning('API duyệt rút tiền chưa được implement trên server');
      } else if (error.response?.status === 500) {
        message.error('Lỗi server khi xử lý duyệt rút tiền');
      } else {
        message.error(`Lỗi kết nối API: ${error.message}`);
      }
    } finally {
      setApprovalLoading(false);
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 70,
      sorter: (a, b) => a.id - b.id
    },
    {
      title: 'Thông tin khách hàng',
      key: 'user_info',
      width: 200,
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>
            <UserOutlined style={{ marginRight: 4, color: '#1890ff' }} />
            {record.user?.name || 'N/A'}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            📱 {record.user?.phone || 'N/A'}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            ✉️ {record.user?.email || 'N/A'}
          </div>
        </div>
      )
    },
    {
      title: 'Thông tin rút tiền',
      key: 'withdrawal_info',
      width: 280,
      render: (_, record) => (
        <div>
          <div style={{ fontSize: '12px', color: '#666', marginBottom: 4 }}>
            Trước: <span style={{ color: '#1890ff', fontWeight: 500 }}>
              {record.withdrawal?.amount_before?.toLocaleString() || 0} VNĐ
            </span>
          </div>
          <div style={{ fontSize: '12px', color: '#666', marginBottom: 4 }}>
            Sau: <span style={{ color: '#52c41a', fontWeight: 500 }}>
              {record.withdrawal?.amount_after?.toLocaleString() || 0} VNĐ
            </span>
          </div>
          <div style={{ fontWeight: 500, color: '#e67e22', marginBottom: 4 }}>
            <BankOutlined style={{ marginRight: 4 }} />
            Rút: {record.withdrawal?.withdrawal_amount?.toLocaleString() || 0} VNĐ
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            Loại: {record.withdrawal?.invoice_type || 'N/A'}
          </div>
          {record.withdrawal?.note && (
            <div style={{ fontSize: '12px', color: '#666', marginTop: 2 }}>
              📝 {record.withdrawal.note}
            </div>
          )}
        </div>
      )
    },
    {
      title: 'Thông tin ngân hàng',
      key: 'bank_info',
      width: 200,
      render: (_, record) => (
        <div>
          {record.bank_info ? (
            <>
              <div style={{ fontWeight: 500, marginBottom: 2 }}>
                🏦 {record.bank_info.bank_name || 'N/A'}
              </div>
              <div style={{ fontSize: '12px', color: '#666', marginBottom: 2 }}>
                STK: {record.bank_info.account_number || 'N/A'}
              </div>
              <div style={{ fontSize: '12px', color: '#666', marginBottom: 2 }}>
                Chủ TK: {record.bank_info.account_holder || 'N/A'}
              </div>
              <div style={{ fontSize: '12px', color: '#888' }}>
                Mã NH: {record.bank_info.bank_code || 'N/A'}
              </div>
            </>
          ) : (
            <div style={{ color: '#ccc', fontSize: '12px' }}>
              Chưa có thông tin NH
            </div>
          )}
        </div>
      )
    },
    {
      title: 'Trạng thái',
      key: 'status',
      width: 120,
      render: (_, record) => {
        const status = record.withdrawal?.status;
        let color = 'default';
        let icon = null;
        
        if (status === 'Đợi Duyệt') {
          color = 'processing';
          icon = <CalendarOutlined />;
        } else if (status === 'Đã Duyệt') {
          color = 'success';
          icon = <CheckOutlined />;
        }
        
        return (
          <Tag color={color} icon={icon}>
            {status || 'N/A'}
          </Tag>
        );
      },
      filters: [
        { text: 'Đợi Duyệt', value: 'Đợi Duyệt' },
        { text: 'Đã Duyệt', value: 'Đã Duyệt' }
      ],
      onFilter: (value, record) => record.withdrawal?.status === value
    },
    {
      title: 'Thời gian',
      key: 'timestamps',
      width: 150,
      render: (_, record) => (
        <div>
          <div style={{ fontSize: '12px' }}>
            📅 {record.timestamps?.created_at ? 
              dayjs(record.timestamps.created_at).format('DD/MM/YY HH:mm') : 'N/A'}
          </div>
        </div>
      ),
      sorter: (a, b) => {
        const timeA = a.timestamps?.created_at ? new Date(a.timestamps.created_at) : new Date(0);
        const timeB = b.timestamps?.created_at ? new Date(b.timestamps.created_at) : new Date(0);
        return timeA - timeB;
      }
    },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Tooltip title="Xem chi tiết">
          <Button
            type="default"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetail(record)}
          >
            Chi tiết
          </Button>
        </Tooltip>
      )
    }
  ];

  const handleViewDetail = (record) => {
    Modal.info({
      title: 'Chi tiết yêu cầu rút tiền',
      width: 600,
      content: (
        <div style={{ marginTop: 16 }}>
          <Row gutter={[16, 8]}>
            <Col span={8}><strong>ID:</strong></Col>
            <Col span={16}>{record.id}</Col>
            
            <Col span={8}><strong>Khách hàng:</strong></Col>
            <Col span={16}>{record.user?.name} (#{record.user?.id})</Col>
            
            <Col span={8}><strong>Điện thoại:</strong></Col>
            <Col span={16}>{record.user?.phone}</Col>
            
            <Col span={8}><strong>Email:</strong></Col>
            <Col span={16}>{record.user?.email}</Col>
            
            <Col span={8}><strong>Số tiền trước:</strong></Col>
            <Col span={16}><Text type="default">{record.withdrawal?.amount_before?.toLocaleString()} VNĐ</Text></Col>
            
            <Col span={8}><strong>Số tiền sau:</strong></Col>
            <Col span={16}><Text type="success">{record.withdrawal?.amount_after?.toLocaleString()} VNĐ</Text></Col>
            
            <Col span={8}><strong>Số tiền rút:</strong></Col>
            <Col span={16}><Text type="warning" strong>{record.withdrawal?.withdrawal_amount?.toLocaleString()} VNĐ</Text></Col>
            
            <Col span={8}><strong>Loại hóa đơn:</strong></Col>
            <Col span={16}>{record.withdrawal?.invoice_type}</Col>
            
            <Col span={8}><strong>Trạng thái:</strong></Col>
            <Col span={16}>
              <Tag color={record.withdrawal?.status === 'Đã Duyệt' ? 'success' : 'processing'}>
                {record.withdrawal?.status}
              </Tag>
            </Col>
            
            <Col span={24} style={{ marginTop: 16, marginBottom: 8 }}>
              <strong style={{ fontSize: '16px', color: '#1890ff' }}>Thông tin ngân hàng:</strong>
            </Col>
            
            {record.bank_info ? (
              <>
                <Col span={8}><strong>Ngân hàng:</strong></Col>
                <Col span={16}>{record.bank_info.bank_name} ({record.bank_info.bank_code})</Col>
                
                <Col span={8}><strong>Số tài khoản:</strong></Col>
                <Col span={16}><Text copyable>{record.bank_info.account_number}</Text></Col>
                
                <Col span={8}><strong>Chủ tài khoản:</strong></Col>
                <Col span={16}>{record.bank_info.account_holder}</Col>
              </>
            ) : (
              <Col span={24}>
                <Text type="secondary">Chưa có thông tin ngân hàng</Text>
              </Col>
            )}
            
            <Col span={24} style={{ marginTop: 16, marginBottom: 8 }}>
              <strong style={{ fontSize: '16px', color: '#1890ff' }}>Thông tin thời gian:</strong>
            </Col>
            
            <Col span={8}><strong>Thời gian tạo:</strong></Col>
            <Col span={16}>
              {record.timestamps?.created_at ? 
                dayjs(record.timestamps.created_at).format('DD/MM/YYYY HH:mm:ss') : 'N/A'}
            </Col>
            
            {record.withdrawal?.note && (
              <>
                <Col span={8}><strong>Ghi chú:</strong></Col>
                <Col span={16}>{record.withdrawal.note}</Col>
              </>
            )}
          </Row>
        </div>
      )
    });
  };

  const handleSearch = () => {
    fetchWithdrawals();
  };

  const handleReset = () => {
    setDateRange([]);
    setStatusFilter('');
    // Reload data
    setTimeout(() => {
      fetchWithdrawals();
    }, 100);
  };

  const handleExportExcel = () => {
    try {
      // Prepare data for CSV export (safer alternative to XLSX)
      const csvData = data.map((item, index) => ({
        'STT': index + 1,
        'ID': item.id,
        'Tên khách hàng': item.user?.name || 'N/A',
        'Số điện thoại': item.user?.phone || 'N/A',
        'Số tiền trước': item.withdrawal?.amount_before?.toLocaleString() || '0',
        'Số tiền sau': item.withdrawal?.amount_after?.toLocaleString() || '0',
        'Số tiền rút': item.withdrawal?.withdrawal_amount?.toLocaleString() || '0',
        'Trạng thái': item.withdrawal?.status || 'N/A',
        'Ngân hàng': item.bank_info?.bank_name || 'N/A',
        'Số tài khoản': item.bank_info?.account_number || 'N/A',
        'Chủ tài khoản': item.bank_info?.account_holder || 'N/A',
        'Mã ngân hàng': item.bank_info?.bank_code || 'N/A',
        'Ghi chú': item.withdrawal?.note || 'N/A',
        'Thời gian tạo': item.timestamps?.created_at ? 
          dayjs(item.timestamps.created_at).format('DD/MM/YYYY HH:mm:ss') : 'N/A'
      }));

      // Convert to CSV format
      const headers = Object.keys(csvData[0]);
      const csvContent = [
        headers.join(','),
        ...csvData.map(row => headers.map(header => `"${row[header]}"`).join(','))
      ].join('\n');

      // Create and download CSV file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      
      const now = dayjs().format('DD-MM-YYYY_HH-mm');
      const filename = `Danh_sach_rut_tien_${now}.csv`;
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      message.success(`Xuất file CSV thành công: ${filename}`);
      
    } catch (error) {
      console.error('Error exporting CSV:', error);
      message.error('Lỗi khi xuất file CSV');
    }
  };

  const pendingCount = data.filter(item => item.withdrawal?.status === 'Đợi Duyệt').length;

  return (
    <div style={{ padding: '24px', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <Card style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <Title level={4} style={{ margin: 0, color: '#1890ff' }}>
            <BankOutlined style={{ marginRight: 8 }} />
            Quản Lý Rút Tiền
          </Title>
          <Space>
            <Button 
              type="default"
              icon={<FileExcelOutlined />}
              onClick={handleExportExcel}
              disabled={data.length === 0}
            >
              Xuất CSV ({data.length})
            </Button>
            <Button 
              type="primary" 
              icon={<ReloadOutlined />}
              onClick={fetchWithdrawals}
              loading={loading}
            >
              Tải lại
            </Button>
          </Space>
        </div>

        {/* Statistics */}
        <Row gutter={16} style={{ marginBottom: '24px' }}>
          <Col xs={24} sm={12} md={6}>
            <Card size="small">
              <Statistic
                title="Tổng yêu cầu"
                value={summary.total_records}
                prefix={<BankOutlined style={{ color: '#1890ff' }} />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card size="small">
              <Statistic
                title="Đợi duyệt"
                value={summary.pending_count}
                prefix={<Badge status="processing" />}
                valueStyle={{ color: '#faad14' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card size="small">
              <Statistic
                title="Đã duyệt"
                value={summary.approved_count}
                prefix={<Badge status="success" />}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card size="small">
              <Statistic
                title="Tổng tiền chờ duyệt"
                value={summary.pending_amount}
                suffix="VNĐ"
                prefix={<BankOutlined style={{ color: '#faad14' }} />}
                valueStyle={{ color: '#faad14' }}
              />
            </Card>
          </Col>
        </Row>

        {/* Filters */}
        <Card size="small" title="Bộ lọc" style={{ marginBottom: '16px' }}>
          <Row gutter={16} align="middle">
            <Col xs={24} sm={12} md={8}>
              <div style={{ marginBottom: '8px' }}>
                <Text strong>Khoảng thời gian:</Text>
              </div>
              <RangePicker
                value={dateRange}
                onChange={setDateRange}
                style={{ width: '100%' }}
                format="DD/MM/YYYY"
                placeholder={['Từ ngày', 'Đến ngày']}
              />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <div style={{ marginBottom: '8px' }}>
                <Text strong>Trạng thái:</Text>
              </div>
              <Select
                value={statusFilter}
                onChange={setStatusFilter}
                style={{ width: '100%' }}
                placeholder="Chọn trạng thái"
                allowClear
              >
                <Select.Option value="Đợi Duyệt">Đợi Duyệt</Select.Option>
                <Select.Option value="Đã Duyệt">Đã Duyệt</Select.Option>
              </Select>
            </Col>
            <Col xs={24} sm={24} md={8}>
              <div style={{ marginBottom: '8px' }}>
                <Text strong>Thao tác:</Text>
              </div>
              <Space wrap>
                <Button 
                  type="primary" 
                  icon={<SearchOutlined />}
                  onClick={handleSearch}
                  loading={loading}
                >
                  Tìm kiếm
                </Button>
                <Button onClick={handleReset}>
                  Đặt lại
                </Button>
                <Button 
                  type="default"
                  icon={<FileExcelOutlined />}
                  onClick={handleExportExcel}
                  disabled={data.length === 0}
                >
                  Xuất CSV
                </Button>
                <Button 
                  type="primary"
                  danger
                  icon={<CheckOutlined />}
                  onClick={() => setApprovalModalVisible(true)}
                  disabled={pendingCount === 0}
                >
                  Duyệt hàng loạt ({pendingCount})
                </Button>
              </Space>
            </Col>
          </Row>
        </Card>



        {/* Table */}
        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1400 }}
          pagination={{
            total: data.length,
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} bản ghi`
          }}
          size="middle"
        />
      </Card>

      {/* Approval Modal */}
      <Modal
        title="Duyệt rút tiền hàng loạt"
        open={approvalModalVisible}
        onCancel={() => {
          setApprovalModalVisible(false);
          setApprovalNote('');
        }}
        width={600}
        footer={[
          <Button key="cancel" onClick={() => {
            setApprovalModalVisible(false);
            setApprovalNote('');
          }}>
            Hủy
          </Button>,
          <Button 
            key="simulate" 
            type="default"
            onClick={handleApproveWithdrawalsSimulate}
            disabled={approvalLoading}
          >
            Mô phỏng duyệt
          </Button>,
          <Button 
            key="approve" 
            type="primary"
            onClick={handleApproveWithdrawals}
            loading={approvalLoading}
          >
            Duyệt thực tế
          </Button>
        ]}
      >
        <div style={{ marginBottom: 16 }}>
          <Text>
            Bạn có chắc chắn muốn duyệt <strong>{pendingCount}</strong> yêu cầu rút tiền đang chờ duyệt?
          </Text>
          {dateRange.length === 2 && (
            <div style={{ marginTop: 8 }}>
              <Text type="secondary">
                Trong khoảng thời gian: {dateRange[0].format('DD/MM/YYYY')} - {dateRange[1].format('DD/MM/YYYY')}
              </Text>
            </div>
          )}
        </div>
        
        <div>
          <Text strong>Ghi chú khi duyệt:</Text>
          <TextArea
            value={approvalNote}
            onChange={(e) => setApprovalNote(e.target.value)}
            placeholder="Nhập ghi chú khi duyệt (tùy chọn)..."
            maxLength={500}
            rows={4}
            style={{ marginTop: 8 }}
          />
          <div style={{ textAlign: 'right', marginTop: 4 }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {approvalNote.length}/500 ký tự
            </Text>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default WithdrawalList;